import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { get, isEmpty, negate } from "lodash";
import moment from "moment";
import { ChevronDown } from "@sonder/ui/source/icons";
import { Dropdown } from "@sonder/ui/source/styled/components/Dropdown";
import { DropdownContent } from "@sonder/ui/source/styled/components/DropdownContent";
import {
  Modal,
  ModalHeader,
  ModalProps,
} from "@sonder/ui/source/styled/components/Modal";
import { Button } from "@sonder/ui/source/styled/fragments/Button";
import { ButtonList } from "@sonder/ui/source/styled/fragments/ButtonList";
import { InputField } from "@sonder/ui/source/styled/fragments/InputField";
import { Label } from "@sonder/ui/source/styled/fragments/Label";
import { ModalFooter } from "@sonder/ui/source/styled/fragments/Modal";
import { PlainButton } from "@sonder/ui/source/styled/fragments/PlainButton";
import { PrimaryButton } from "@sonder/ui/source/styled/fragments/PrimaryButton";
import { TextArea } from "@sonder/ui/source/styled/fragments/TextArea";
import { borders, colors } from "@sonder/ui/source/styled/theme";
import { useSegment } from "analytics/segment";
import { useErrorReporterFiltered } from "core/hooks/useErrorReporterFiltered";
import { useCreateTask } from "core/queries/CreateTask";
import {
  TaskData,
  TaskSubjectType,
  TaskSubjectTypes,
  TaskUnitBookingInfoCurrentBooking,
  TaskUnitBookingInfoPreviousBooking,
  TaskUnitBookingInfoUpcomingBooking,
} from "core/queries/GetTask";
import { PriorityBadge } from "tasks/components/PriorityBadge";
import {
  TaskDueByDate,
  TaskDueByDateTrigger,
} from "tasks/components/TaskDueByDate";
import {
  TaskBooking,
  TaskUnitBookingType,
  TaskUnitBookingTypeLabels,
} from "tasks/components/TaskPanel/Activity/TaskBooking";
import { TaskPriority } from "tasks/components/TaskPriority";
import { TASK_CONTEXT_CUSTOM_UNIQUE_REFS } from "tasks/constants";
import { TypeEnum } from "typings/globalGQLTypes";

const StyledPlainButton = styled(PlainButton)`
  margin-top: 0.5rem;
`;

const StyledTextArea = styled(TextArea)`
  background: ${colors.white};
  border: ${borders.standard};
  border-radius: 3px;
  flex: 1 1 auto;
  padding: 0.55rem 0.7rem;
  width: 100%;
`;

const AddActionForm = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const InputFieldContainer = styled.div`
  align-items: stretch;
  display: flex;
  flex-direction: row;

  ${InputField} {
    flex: 0 1 50%;
  }
`;

const StyledButtonList = styled(ButtonList)`
  border-radius: 3px;
  width: 100%;
`;

interface BookingOption
  extends TaskUnitBookingInfoPreviousBooking,
    TaskUnitBookingInfoCurrentBooking,
    TaskUnitBookingInfoUpcomingBooking {
  type: TaskUnitBookingType;
}

const isInquiryOrLeaseInquiry = (task: TaskData) => {
  const subjectType = task.subjectType as TaskSubjectTypes;
  return [TaskSubjectType.LeaseInquiry, TaskSubjectType.Inquiry].includes(
    subjectType,
  );
};

const getBookingWithType = (
  task: TaskData,
  path: string,
  type:
    | TaskUnitBookingType.Default
    | TaskUnitBookingType.Previous
    | TaskUnitBookingType.Current
    | TaskUnitBookingType.Upcoming,
): BookingOption => {
  const booking = get(task, path, null);
  return booking ? { ...booking, type } : null;
};

const isNotEmpty = negate(isEmpty);

const getBookingOptions = (task: TaskData): BookingOption[] => {
  const booking = getBookingWithType(
    task,
    "booking",
    TaskUnitBookingType.Default,
  );
  const previousBooking = getBookingWithType(
    task,
    "unit.bookingInfo.previousBooking",
    TaskUnitBookingType.Previous,
  );
  const currentBooking = getBookingWithType(
    task,
    "unit.bookingInfo.currentBooking",
    TaskUnitBookingType.Current,
  );
  const upcomingBooking = getBookingWithType(
    task,
    "unit.bookingInfo.upcomingBooking",
    TaskUnitBookingType.Upcoming,
  );

  return isInquiryOrLeaseInquiry(task)
    ? [booking]
    : [previousBooking, currentBooking, upcomingBooking].filter(isNotEmpty);
};

interface AddActionModalType extends Pick<ModalProps, "isOpen" | "onClose"> {
  task: TaskData;
}

export const AddActionModal = ({
  task,
  isOpen,
  onClose,
}: AddActionModalType) => {
  const errorReporter = useErrorReporterFiltered();
  const { track } = useSegment();
  const [dueAt, setDueAt] = useState<moment.Moment | null>(null);
  const [priority, setPriority] = useState<number | null>(null);
  const [recipient] = useState("CGS");
  const [notes, setNotes] = useState("");
  const bookings = getBookingOptions(task);
  const hasValidBookings = isNotEmpty(bookings);
  const isBookingSelectionDisabled = bookings.length <= 1;
  const defaultBookingId = get(bookings, "[0].id", null);
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(
    defaultBookingId,
  );
  const selectedBooking = bookings.find(
    booking => booking.id === selectedBookingId,
  );
  useEffect(
    () => () => {
      if (!isOpen) {
        setNotes("");
        setSelectedBookingId(defaultBookingId);
      }
    },
    [isOpen],
  );
  const bookingTrigger =
    selectedBooking && selectedBooking.type ? (
      <TaskBooking
        iconRight
        booking={selectedBooking}
        bookingLabel={TaskUnitBookingTypeLabels[selectedBooking.type]}
        disabled={isBookingSelectionDisabled}
      >
        {!isBookingSelectionDisabled && <ChevronDown />}
      </TaskBooking>
    ) : (
      <Button
        fullWidth
        iconRight
        disabled={isBookingSelectionDisabled}
        type="button"
      >
        <span>
          {hasValidBookings ? "Select a Booking" : "No Bookings available."}
        </span>
        {!isBookingSelectionDisabled && <ChevronDown />}
      </Button>
    );

  // Validation
  const [onCreateTask, { loading: isSubmitting }] = useCreateTask({
    refetchQueries: ["GetTaskComments"],
    awaitRefetchQueries: true,
  });

  const isSubmitDisabled = isSubmitting || !selectedBooking || !notes.trim();

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      {({ closeModal }) => {
        const onHandleClick = async () => {
          if (!selectedBooking) {
            return;
          }
          try {
            const subjectId = selectedBooking.id;
            await onCreateTask({
              variables: {
                dueAt,
                priority,
                name: `CGS: Contact ${
                  TaskUnitBookingTypeLabels[selectedBooking.type]
                } Guest`.replace("  ", " "),
                subjectId,
                sourceTaskId: task.id,
                tomTaskContextCustomUniqueRef:
                  TASK_CONTEXT_CUSTOM_UNIQUE_REFS.CGS_ACTION_REQUIRED,
                type: TypeEnum.Tom_Tasks_Booking,
                notes,
              },
            });
            track({
              eventName: "task_panel",
              action: "click",
              element: "add_action_modal_submit",
              props: {
                taskId: task.id,
                subjectId,
                type: TypeEnum.Tom_Tasks_Booking,
              },
            });
            closeModal();
          } catch (error) {
            errorReporter.notify(error);
          }
        };
        return (
          <>
            <ModalHeader title="Add Action" onClose={closeModal} />
            <AddActionForm>
              <InputField>
                <Label>Recipient</Label>
                <Dropdown
                  trigger={
                    <Button disabled fullWidth type="button">
                      {recipient}
                    </Button>
                  }
                >
                  {({ isOpen: isPortalOpen, closePortal }) =>
                    isPortalOpen && (
                      <DropdownContent>
                        <StyledButtonList>
                          <Button
                            fullWidth
                            noBorder
                            selected
                            type="button"
                            onClick={() => closePortal()}
                          >
                            {recipient}
                          </Button>
                        </StyledButtonList>
                      </DropdownContent>
                    )
                  }
                </Dropdown>
              </InputField>
              <InputField>
                <Label>Action</Label>
                {!isBookingSelectionDisabled ? (
                  <Dropdown trigger={bookingTrigger}>
                    {({ isOpen: isPortalOpen, closePortal }) =>
                      isPortalOpen && (
                        <DropdownContent>
                          <StyledButtonList>
                            {bookings.map(booking =>
                              booking ? (
                                <TaskBooking
                                  noBorder
                                  booking={booking}
                                  bookingLabel={
                                    TaskUnitBookingTypeLabels[booking.type]
                                  }
                                  key={booking.id}
                                  selected={booking.id === selectedBookingId}
                                  onClick={() => {
                                    setSelectedBookingId(booking.id);
                                    closePortal();
                                  }}
                                />
                              ) : null,
                            )}
                          </StyledButtonList>
                        </DropdownContent>
                      )
                    }
                  </Dropdown>
                ) : (
                  bookingTrigger
                )}
              </InputField>
              <InputFieldContainer>
                <InputField>
                  <Label>Due At</Label>
                  <TaskDueByDate
                    isTurnoverDisabled
                    alignContent="left"
                    bookingInfo={null}
                    date={dueAt || null}
                    eventName="task_panel"
                    futureCheckouts={[]}
                    recurrenceSetting={null}
                    renderTrigger={props => <TaskDueByDateTrigger {...props} />}
                    taskId={task.id}
                    onChange={variables => setDueAt(variables?.dueBy)}
                  />
                </InputField>
                <InputField>
                  <Label>Priority</Label>
                  <TaskPriority
                    alignContent="center"
                    eventName="task_panel"
                    priority={priority || null}
                    taskId={task.id}
                    trigger={
                      <StyledPlainButton title="Priority" type="button">
                        <PriorityBadge priority={priority || null} />
                      </StyledPlainButton>
                    }
                    onSetPriority={newPriority => setPriority(newPriority)}
                  />
                </InputField>
              </InputFieldContainer>
              <InputField>
                <Label>Notes</Label>
                <StyledTextArea
                  minRows={3}
                  placeholder="Enter Text"
                  type="text"
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                />
              </InputField>
              <ModalFooter>
                <PrimaryButton
                  disabled={isSubmitDisabled}
                  onClick={onHandleClick}
                >
                  {isSubmitting ? "Sending..." : "Send"}
                </PrimaryButton>
              </ModalFooter>
            </AddActionForm>
          </>
        );
      }}
    </Modal>
  );
};
